import React, { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Camera, X } from 'lucide-react';
import { getImageUrl } from '../../utils/imageOptimization';
import { isVenueClaimed } from './venueHelpers';

const VenueGallery = React.memo(({ venue, selectedImage, setSelectedImage, isLightboxOpen, setIsLightboxOpen }) => {
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = useCallback((e) => {
    setTouchStart(e.targetTouches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!venue?.images) return;
    const diff = touchStart - touchEnd;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setSelectedImage(selectedImage === venue.images.length - 1 ? 0 : selectedImage + 1);
      } else {
        setSelectedImage(selectedImage === 0 ? venue.images.length - 1 : selectedImage - 1);
      }
    }
  }, [touchStart, touchEnd, venue, selectedImage, setSelectedImage]);

  if (!venue?.images || venue.images.length === 0) return null;

  return (
    <>
      {/* Carousel Gallery Card */}
      <div className="bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
        <div 
          className="relative bg-black group overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Main Image */}
          <div className="relative cursor-pointer h-[350px] sm:h-[420px] lg:h-[520px]" onClick={() => setIsLightboxOpen(true)}>
            <div 
              className="absolute inset-0 scale-125"
              style={{
                backgroundImage: `url(${getImageUrl(venue.images[selectedImage])})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(40px) brightness(0.5)',
              }}
            />
            <img 
              src={getImageUrl(venue.images[selectedImage])}
              alt={`${venue.name} - Fotoğraf ${selectedImage + 1}`}
              className="w-full h-full object-contain relative z-10"
              loading={selectedImage === 0 ? "eager" : "lazy"}
              fetchpriority={selectedImage === 0 ? "high" : "auto"}
            />
            {!isVenueClaimed(venue) && venue.source === 'google' && (
              <div className="absolute bottom-20 right-4 z-20 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-gray-600 px-3 py-1.5 rounded-full text-xs font-medium shadow-md">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Google
              </div>
            )}
          </div>

          {/* Navigation Arrows */}
          {venue.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage(selectedImage === 0 ? venue.images.length - 1 : selectedImage - 1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-md text-gray-800 p-3 rounded-2xl shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 z-20"
                aria-label="Önceki fotoğraf"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage(selectedImage === venue.images.length - 1 ? 0 : selectedImage + 1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-md text-gray-800 p-3 rounded-2xl shadow-xl opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 z-20"
                aria-label="Sonraki fotoğraf"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Bottom Bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent backdrop-blur-sm p-6 z-20">
            <div className="flex items-center justify-between">
              <div className="bg-white/20 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-bold border border-white/30">
                {selectedImage + 1} / {venue.images.length}
              </div>
              {venue.images.length > 1 && venue.images.length <= 10 && (
                <div className="flex items-center gap-2">
                  {venue.images.map((_, index) => (
                    <button
                      key={index}
                      onClick={(e) => { e.stopPropagation(); setSelectedImage(index); }}
                      className={`rounded-full transition-all duration-300 ${
                        index === selectedImage 
                          ? 'w-8 h-3 bg-white shadow-lg' 
                          : 'w-3 h-3 bg-white/50 hover:bg-white/70'
                      }`}
                      aria-label={`Fotoğraf ${index + 1}`}
                    />
                  ))}
                </div>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setIsLightboxOpen(true); }}
                className="flex items-center gap-2 bg-white/20 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-bold border border-white/30 hover:bg-white/30 transition-all duration-300"
              >
                <Camera className="w-4 h-4" />
                <span className="hidden sm:inline">Galeri</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center" onClick={() => setIsLightboxOpen(false)}>
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 bg-white/10 backdrop-blur-md rounded-full z-50 transition-all hover:bg-white/20"
            onClick={() => setIsLightboxOpen(false)}
          >
            <X className="w-8 h-8" />
          </button>
          <div className="w-full h-full flex items-center justify-center p-4 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(venue.images[selectedImage])}
              alt={`${venue.name} - Fotoğraf ${selectedImage + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
          {venue.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage(selectedImage === 0 ? venue.images.length - 1 : selectedImage - 1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-md text-white p-3 rounded-2xl hover:bg-white/20 transition-all"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedImage(selectedImage === venue.images.length - 1 ? 0 : selectedImage + 1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-md text-white p-3 rounded-2xl hover:bg-white/20 transition-all"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          {/* Thumbnails */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto p-2 bg-black/40 backdrop-blur-md rounded-2xl">
            {venue.images.map((img, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setSelectedImage(index); }}
                className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 transition-all ${
                  index === selectedImage ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
});

VenueGallery.displayName = 'VenueGallery';
export default VenueGallery;
